import React, { FC, useEffect, useMemo, useState } from 'react';

import { styled, TextField } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { TFunction } from 'i18next';

import { QuizColorsMode } from '~/blocks/TestCard/TestCard';
import Autocomplete from '~/components/Autocomplete/Autocomplete';
import {
  BackButton,
  Buttons,
  ExplanationText,
  StyledChevronLeftIcon,
  StyledSmallTitle,
  SubmitButton,
  TitleWrapper,
} from '~/components/QuestionQuiz/styles';
import {
  reportValidationSchema,
  reportWithoutMessageValidationSchema,
} from '~/components/QuestionQuiz/validation';
import UnstyledButton from '~/components/UI/UnstyledButton';
import useReport from '~/hooks/useReport';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import useValidation from '~/hooks/useValidation';
import { MIDDLE_MARGIN_PX, SUBMIDDLE_MARGIN_PX } from '~/theme';
import sendAnalyticEvent from '~/utils/sendAnalyticEvent';

const createReportOptions = (t: TFunction) => [
  { display: t('page.video.questionIsUnclear'), value: 1 },
  { display: t('page.video.grammaticalErrors'), value: 2 },
  { display: t('page.video.questionIsNotRelated'), value: 3 },
  { display: t('page.video.answerShouldHaveBeenAccepted'), value: 4 },
  { display: t('page.video.correctAnswerIsNotAmong'), value: 5 },
  { display: t('page.video.explanationIsInsufficient'), value: 6 },
  { display: t('page.video.somethingWentWrong'), value: 7 },
];

interface IReportForm {
  relatableEntityId: string;
  onCloseForm: () => void;
  hideArrowBackButton: boolean;
  controlledReport: ReturnType<typeof useReport>;
  colorsMode?: QuizColorsMode;
}

const ReportForm: FC<IReportForm> = ({
  relatableEntityId,
  onCloseForm,
  hideArrowBackButton,
  controlledReport,
  colorsMode,
}) => {
  const { t } = useTenantTranslation();
  const [showMessageField, setShowMessageField] = useState(false);

  const { submit: sendReport, loading: sendReportLoading } = controlledReport;
  const reportOptions = useMemo(() => createReportOptions(t), [t]);
  const { handleAutocompleteChange, errors, values, handleChange, handleSubmit, resetToDefault } =
    useValidation(
      {
        message: '',
        request_type: null,
      },
      showMessageField ? reportValidationSchema : reportWithoutMessageValidationSchema,
      onSubmit,
    );

  useEffect(() => {
    setShowMessageField(values.request_type === reportOptions.at(6));
  }, [setShowMessageField, reportOptions, values]);

  async function onSubmit(values) {
    await sendReport({
      message: showMessageField ? values.message : undefined,
      requestTypeId: values.request_type.value,
      relatableEntityId: Number(relatableEntityId),
    });
    onCloseForm();
    resetToDefault();
    sendAnalyticEvent('Video_post_KC_Flag_submit');
  }

  return (
    <>
      {!hideArrowBackButton && (
        <BackButton
          disabled={sendReportLoading}
          aria-label={t('button.close')}
          onClick={onCloseForm}
        >
          <StyledChevronLeftIcon />
        </BackButton>
      )}
      <TitleWrapper>
        <StyledSmallTitle colorsMode={colorsMode} variant="h2">
          {t('page.video.whatIssue')}
        </StyledSmallTitle>
      </TitleWrapper>
      <ExplanationText colorsMode={colorsMode}>{t('page.video.sorryToHear')}</ExplanationText>
      <StyledAutocomplete
        data-testid="reportField"
        options={reportOptions}
        getOptionLabel={(option) => option?.display || ''}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            name="request_type"
            variant="outlined"
            label={t('input.label.selectAnIssue')}
            error={Boolean(errors?.request_type)}
            helperText={errors?.request_type}
            colorsMode={colorsMode}
          />
        )}
        value={values.request_type}
        onChange={handleAutocompleteChange('request_type')}
      />
      {showMessageField && (
        <StyledTextArea
          name="message"
          inputProps={{
            'data-testid': 'reportMessage',
          }}
          label={t('input.label.describeIssue')}
          value={values.message}
          onChange={handleChange}
          error={Boolean(errors?.message)}
          helperText={errors?.message}
          multiline
          rows={3}
          colorsMode={colorsMode}
        />
      )}
      <Buttons>
        <StyledCancelButton
          data-testid="closeReport"
          disabled={sendReportLoading}
          onClick={onCloseForm}
          colorsMode={colorsMode}
        >
          {t('button.cancel')}
        </StyledCancelButton>
        <SubmitButton
          data-testid="submitReport"
          disabled={sendReportLoading}
          onClick={handleSubmit}
          colorsMode={colorsMode}
        >
          {t('button.report')}
        </SubmitButton>
      </Buttons>
    </>
  );
};

export default ReportForm;

const StyledAutocomplete = styled(Autocomplete)`
  width: 100%;
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
`;
const TextArea = styled(TextField)`
  width: 100%;
  margin-bottom: auto;
`;
const StyledCancelButton = styled(UnstyledButton, {
  shouldForwardProp: (prop) => prop !== 'colorsMode',
})<{ colorsMode?: QuizColorsMode }>`
  text-transform: uppercase;
  margin: ${SUBMIDDLE_MARGIN_PX} auto 0;
  font-weight: bold;
  font-size: 16px;
  line-height: 20px;
  cursor: pointer;
  color: ${({ theme, colorsMode }) =>
    colorsMode === QuizColorsMode.PostVideo
      ? theme.palette.common.text.white
      : theme.palette.common.white};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-top: 0;
    margin-left: auto;
    margin-right: ${MIDDLE_MARGIN_PX};
  }

  &:hover {
    color: ${({ theme }) => theme.palette.primary.light};
  }
`;
const createStyledTextField = (tag) => styled(tag, {
  shouldForwardProp: (prop) => prop !== 'colorsMode',
})<{ colorsMode?: QuizColorsMode }>`
  &.MuiFormControl-root {
    & .MuiInputLabel-root {
      color: ${({ theme, colorsMode }) =>
        alpha(
          colorsMode === QuizColorsMode.PostVideo
            ? theme.palette.common.text.white
            : theme.palette.common.white,
          0.33,
        )};
    }

    & .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme, colorsMode }) =>
        alpha(
          colorsMode === QuizColorsMode.PostVideo
            ? theme.palette.common.text.white
            : theme.palette.common.white,
          0.33,
        )};
    }

    & .MuiAutocomplete-popupIndicator {
      color: ${({ theme, colorsMode }) =>
        alpha(
          colorsMode === QuizColorsMode.PostVideo
            ? theme.palette.common.text.white
            : theme.palette.common.white,
          0.33,
        )};
    }

    & .MuiInputBase-input.MuiOutlinedInput-input {
      color: ${({ theme, colorsMode }) =>
        colorsMode === QuizColorsMode.PostVideo
          ? theme.palette.common.text.secondaryToWhite
          : theme.palette.common.text.secondaryToPrimary};
    }
  }
`;
const StyledTextField = createStyledTextField(TextField);
const StyledTextArea = styled(createStyledTextField(TextArea))`
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;
